import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { css } from "styled-components";

const enum fields {
    youtubeSource = "youtubeSource",
    videoSource = "videoSource",
    vimeoSource = "vimeoSource",
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    videoFormat = "videoFormat",
    title = "title",
    paddingTop = "paddingTop",
    paddingBottom = "paddingBottom",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.youtubeSource]: string;
        [fields.videoSource]: string;
        [fields.vimeoSource]: string;
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.title]: string;
        [fields.paddingTop]: string;
        [fields.paddingBottom]: string;
        [fields.videoFormat]: "youtube" | "video" | "vimeo";
    };
}

export interface ContainerStyles {
    mainContainer?: InjectableCss;
    container?: InjectableCss;
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
    button?: InjectableCss;
    container?: InjectableCss;
    title?: InjectableCss;
    adminVideo?: InjectableCss;
}

export const containerStyles: ContainerStyles = {};

export const imageStyles: ImageStyles = {
    title: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 600;
            font-size: 28px;
            line-height: 38px;
            text-align: center;
            text-transform: uppercase;
            color: ${getColor("text.main")};
            margin-bottom: 36px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 16px;
                    `,
                    css`
                        margin-bottom: 16px;
                    `,
                    css`
                        margin-bottom: 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    container: {
        css: css`
            max-width: 955px;
            max-height: 537px;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: 200px;
                        padding: 0px 20px;
                        iframe {
                            max-height: 200px;
                        }
                    `,
                    css`
                        max-height: 200px;
                        padding: 0px 20px;
                        iframe {
                            max-height: 200px;
                        }
                    `,
                    css`
                        padding: 0px 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    adminVideo: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: 200px;
                        video {
                            max-height: 200px;
                        }
                    `,
                    css`
                        max-height: 200px;
                        video {
                            max-height: 200px;
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = imageStyles;

const SurVideoContainer: React.FC<OwnProps> = ({ fields }) => {
    const mainContainer = {
        css: css`
            padding-top: ${fields.paddingTop}px;
            padding-bottom: ${fields.paddingBottom}px;
        `,
    };
    return (
        <StyledWrapper {...mainContainer}>
            <StyledWrapper {...styles.title}>{fields.title}</StyledWrapper>
            <StyledWrapper {...styles.container}>
                {fields.youtubeSource && fields.videoFormat === "youtube" && (
                    <StyledWrapper>
                        <iframe
                            width="100%"
                            height="537"
                            src={`https://www.youtube.com/embed/${fields.youtubeSource}`}
                            title="YouTube video player"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        ></iframe>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                {fields.videoSource && fields.videoFormat === "video" && (
                    <StyledWrapper {...styles.adminVideo}>
                        <video width="100%" height="537" controls>
                            <track kind="captions" />
                            <source src={`${fields.videoSource}#t=0.001`} type="video/mp4" />
                        </video>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                {fields.vimeoSource && fields.videoFormat === "vimeo" && (
                    <StyledWrapper>
                        <StyledWrapper>
                            <iframe
                                width="100%"
                                height="537"
                                src={`https://player.vimeo.com/video/${fields.vimeoSource}`}
                                title="Vimeo video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            ></iframe>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurVideoContainer,
    definition: {
        group: "Common",
        displayName: "SurVideoContainer",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.paddingTop,
                displayName: "Padding Top",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.paddingBottom,
                displayName: "Padding Bottom",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.videoFormat,
                fieldType: "General",
                displayName: "Image Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "youtube", value: "youtube" },
                    { displayName: "video", value: "video" },
                    { displayName: "vimeo", value: "vimeo" },
                ],
                defaultValue: "youtube",
            },
            {
                fieldType: "General",
                name: fields.youtubeSource,
                editorTemplate: "MultilineTextField",
                displayName: "Youtube URL",
                defaultValue: "",
                isVisible: item => item.fields[fields.videoFormat] === "youtube",
            },
            {
                fieldType: "General",
                name: fields.videoSource,
                editorTemplate: "ImagePickerField",
                displayName: "Video URL",
                defaultValue: "",
                isVisible: item => item.fields[fields.videoFormat] === "video",
            },
            {
                fieldType: "General",
                name: fields.vimeoSource,
                editorTemplate: "MultilineTextField",
                displayName: "Vimeo URL",
                defaultValue: "",
                isVisible: item => item.fields[fields.videoFormat] === "vimeo",
            },
        ],
    },
};

export default widgetModule;
